// Redux Toolkit uses Immer, so mutating the `state` draft in reducers is intended.
/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import arClient from '../../util/api_client';
import { isApiSuccess } from '../../constants/platformStatusCodes';
import {
  MEDIA_MODEL_FETCH,
  MEDIA_MODEL_FETCH_BY_MEDIA,
  MEDIA_MODEL_DELETE,
  MEDIA_MODEL_REANALYZE,
} from '../../constants/apiUrls';
import { parseMediaItem, parseMediaList } from './utils/parseMediaRespData';
import { analyzeMediaV2 } from './utils/mediaOptimizationApi';

const initialState = {
  mediaList: [],
  mediaListLoading: true,
};

const MediaOptimizationSlice = createSlice({
  name: 'MediaOptimization',
  initialState,
  reducers: {
    setMediaList(state, action) {
      state.mediaList = action.payload;
    },
    setMediaListLoading(state, action) {
      state.mediaListLoading = action.payload;
    },
    // Replace a single row in place (used by the poll loop).
    upsertMediaItem(state, action) {
      const row = action.payload;
      if (!row || row.id == null) return;
      const idx = state.mediaList.findIndex((m) => m.id === row.id);
      if (idx === -1) {
        state.mediaList.unshift(row);
      } else {
        state.mediaList[idx] = row;
      }
    },
    removeMediaItems(state, action) {
      const ids = action.payload;
      state.mediaList = state.mediaList.filter((m) => !ids.includes(m.id));
    },
  },
});

export const {
  setMediaList,
  setMediaListLoading,
  upsertMediaItem,
  removeMediaItems,
} = MediaOptimizationSlice.actions;

/**
 * Fetch the full media library and map each item into a table row model.
 */
export const fetchMediaList = () => (dispatch) => {
  dispatch(setMediaListLoading(true));

  return new Promise((resolve) => {
    arClient.doApiCall(MEDIA_MODEL_FETCH, {}, (resp) => {
      if (isApiSuccess(resp) && resp.data) {
        dispatch(setMediaList(parseMediaList(resp.data)));
      }
      dispatch(setMediaListLoading(false));
      resolve(resp);
    });
  });
};

/**
 * Fetch a single media item by id (used for polling). Resolves with the parsed
 * row (or null on failure) and updates it in the list on success.
 */
export const fetchMediaByMediaId = (mediaId) => (dispatch) =>
  new Promise((resolve) => {
    arClient.doApiCall(MEDIA_MODEL_FETCH_BY_MEDIA, { mediaId }, (resp) => {
      if (isApiSuccess(resp) && resp.data) {
        const row = parseMediaItem({ ...resp.data, personaName: resp.personaName });
        dispatch(upsertMediaItem(row));
        resolve(row);
      } else {
        resolve(null);
      }
    });
  });

/**
 * Submit a single piece of media for analysis and drop the returned
 * (queued/processing) row into the list — no full-list refetch. Once mounted,
 * its MediaEchoListener updates the row on the completed/failed broadcast.
 * Rejects (via analyzeMediaV2) on failure so the caller can surface the error.
 */
export const analyzeMedia = (input) => async (dispatch) => {
  const resp = await analyzeMediaV2(input);
  const row = parseMediaItem(resp.data);
  dispatch(upsertMediaItem(row));
  return row;
};

/**
 * Re-run analysis on a single media item that failed to analyze. On success the
 * backend returns the requeued (queued/processing) row — upsert it so the row
 * flips back to a processing state and its MediaEchoListener resumes polling.
 */
export const reanalyzeMedia = (mediaId) => (dispatch) =>
  new Promise((resolve) => {
    arClient.doApiCall(MEDIA_MODEL_REANALYZE, { mediaId: Number(mediaId) }, (resp) => {
      if (isApiSuccess(resp) && resp.data) {
        dispatch(upsertMediaItem(parseMediaItem({ ...resp.data, personaName: resp.personaName })));
      }
      resolve(resp);
    });
  });

/**
 * Delete one or more media items and drop them from the list on success.
 */
export const deleteMedia = (mediaIds) => (dispatch) => {
  const ids = (Array.isArray(mediaIds) ? mediaIds : [mediaIds]).map(Number);

  return new Promise((resolve) => {
    arClient.doApiCall(MEDIA_MODEL_DELETE, { mediaIdArray: JSON.stringify(ids) }, (resp) => {
      if (isApiSuccess(resp)) {
        dispatch(removeMediaItems(ids));
      }
      resolve(resp);
    });
  });
};

export default MediaOptimizationSlice.reducer;
